import { useEffect, useRef, useState } from 'react'
import 'maplibre-gl/dist/maplibre-gl.css'
import '../../styles/MiniMap.css'
import '../../styles/MapControls.css'
import { useMinimap } from './useMinimap'
import { useMapExport } from './useMapExport'
import { useKeyboardPan } from './useKeyboardPan'
import { useCenterToRegion } from './useCenterToRegion'
import { useMapFilters } from './useMapFilters'
import { useRuler } from './useRuler'
import { useRangeCircle } from './useRangeCircle'
import { useWorldOverlays } from './useWorldOverlays'
import { useMilitaryUnits } from './useMilitaryUnits'
import { useMilPlan } from './useMilPlan'
import { useStructures } from './useStructures'
import { usePowerPlants } from './usePowerPlants'
import { useInfrastructure } from './useInfrastructure'
import { useOwnedStats } from './useOwnedStats'
import { useWeather } from './useWeather'
import { useProjection } from './useProjection'
import { useMapLayers } from './useMapLayers'
import { useBorders } from './useBorders'
import { useMapInit } from './useMapInit'
import { useNativeControls } from './useNativeControls'
import { useCoordinates } from './useCoordinates'
import { useContextMenu } from './useContextMenu'
import MapContextMenu from '../MapContextMenu'
import { InfraNodeMenu } from '../InfraContextMenus'
import { useViewport } from './useViewport'
import { useMultiSelect } from './useMultiSelect'
import { useLegendIsolate } from './useLegendIsolate'
import { useFocusCountry } from './useFocusCountry'
import { useBubbleMap } from './useBubbleMap'
import { useExtensionLayers } from './useExtensionLayers'
import { useRecentActivity } from './useRecentActivity'
import { useAnnexations } from './useAnnexations'
import CoordinatesDisplay from '../CoordinatesDisplay'
import { DEFAULT_OVERLAY_STYLES } from '../../utils/overlayStyles'

const MapView = ({ layers, filters, onMapLoaded, onRegionClick, selectedRegion, planMode, customColors, selectionMode, onRegionPaint, onRegionErase, onCenterToRegion, onRegionsDataLoaded, isGlobeView, satelliteMode, regionStats, statType, structFilter, weatherData, recentActivity, diplomacyData, annexations, showStructures, showInfrastructure, structuresData, powerPlantsData, infrastructureData, constructionMode, onStructurePlace, onStructureMove, onPowerPlantMove, infrastructureMode, infrastructurePath, onInfrastructureClick, onInfraPointMove, onInfraPointDelete, onInfraPointFork, onInfraPointConnect, infraMode, selectedTroncIds, onTroncSelect, onInfraConnectStart, showMilitary, militaryUnits, onUnitClick, ownedCountryId: ownedCountryIdProp, onUnitMoved, onUnitDropped, milPlanFeatures, milPlanActive, milPlanTool, onMilPlanClick, onMilPlanGesture, selectedMilPlanFeature, onMilPlanFeatureSelect, onMilPlanFeatureChange, milPlanTextDraft, milPlanTextStyle, onMilPlanTextCommit, onMilPlanTextCancel, borderWidth, borderColor, borderMode, borderOpacity, borderStyle, haloCountryIds, haloColor, mapBackground, rulerMode, rulerPath, onRulerClick, onRulerPointMove, onRulerPointDelete, rangeMode, rangeCenter, rangeRadiusKm, onRangeClick, organizationsCatalog, selectedOrgId, wars, selectedWarId, showToast, showCities, showRoads, showRailways, showHydrography, showBubbles, bubbleMetric, bubbleColor, bubbleSize, overlayStyles, onOpenSettings, embedMode }) => {
  const styles = overlayStyles || DEFAULT_OVERLAY_STYLES
  const mapContainer = useRef(null)
  const map = useRef(null)
  const [regionsData, setRegionsData] = useState(null)
  // Bumped on zoomend so the structures clustering recomputes at the new
  // scale (clusters depend on zoom only — panning keeps relative distances).
  const [zoomBucket, setZoomBucket] = useState(2)
  const [regionsMercatorData, setRegionsMercatorData] = useState(null) // Mercator version (±85° clamp)
  const [regionsGlobeData, setRegionsGlobeData] = useState(null) // Globe version (no clamp)
  const previousCustomColorsRef = useRef({}) // Track previous colors for efficient diff (shared with useMapLayers)

  useMapInit(map, mapContainer, previousCustomColorsRef, { isGlobeView, onRegionsDataLoaded, onRegionClick, onRulerClick, onRangeClick, onInfrastructureClick, onStructurePlace, onRegionPaint, onRegionErase, onMapLoaded, setRegionsData, setRegionsMercatorData, setRegionsGlobeData, setZoomBucket, mapBackground })

  useNativeControls(map, { allowFullscreen: !embedMode })
  useViewport(map)
  useMultiSelect(map, regionsData)
  useLegendIsolate(map, regionsData)
  useFocusCountry(map, regionsData)
  useBubbleMap(map, { showBubbles, bubbleMetric, bubbleColor, bubbleSize, regionsData })
  // Extension point (S15): future gameplay systems register layers here.
  useExtensionLayers(map, { regionsData, layers })
  const cursorCoords = useCoordinates(map)
  const { menu: contextMenu, closeMenu } = useContextMenu(map)

  // Update cursor for Plan Mode / Construction Mode / Infrastructure Mode.
  // Also focus the canvas so MapLibre's keyboard handler (arrows to pan,
  // +/- to zoom) works while drawing — dragPan is hijacked by the stroke,
  // keyboard becomes the always-available navigation.
  useEffect(() => {
    if (!map.current) return
    const canvas = map.current.getCanvas()
    // The infra tool only shows the drawing crosshair in its 'draw' sub-mode;
    // 'select' keeps the plain pointer, because there the mouse navigates.
    const drawing = planMode || constructionMode || rulerMode || rangeMode
      || (infrastructureMode && infraMode === 'draw')
    canvas.style.cursor = drawing ? 'crosshair' : ''
    if (constructionMode || infrastructureMode || rulerMode || rangeMode) {
      map.current.keyboard.enable()
      canvas.focus()
    }
  }, [planMode, constructionMode, infrastructureMode, infraMode, rulerMode, rangeMode])
  
  useBorders(map, { satelliteMode, isGlobeView, borderColor, borderWidth, borderMode, borderOpacity, borderStyle, haloCountryIds, haloColor, mapBackground, regionsData })

  // Ocean/background colour follows the theme (whitish in light mode).
  useEffect(() => {
    if (!map.current) return
    const apply = () => {
      if (map.current.getLayer('background')) {
        map.current.setPaintProperty('background', 'background-color', mapBackground || '#1a1a1a')
      }
    }
    if (map.current.isStyleLoaded()) apply()
    else map.current.once('idle', apply)
  }, [mapBackground])

  useProjection(map, mapContainer, { isGlobeView, regionsMercatorData, regionsGlobeData, onRegionsDataLoaded, setRegionsData, mapBackground })

  useMapLayers(map, previousCustomColorsRef, { regionsData, planMode, customColors, layers, statType, regionStats, organizationsCatalog, selectedOrgId, wars, selectedWarId, diplomacyData })



  useOwnedStats(map, { regionStats, structFilter, regionsData, regionsMercatorData, regionsGlobeData })

  useWeather(map, { weatherData, weatherLayerActive: layers.weather, planMode, regionsData, regionsMercatorData, regionsGlobeData })
  useRecentActivity(map, { active: !!layers.recentActivity, rows: recentActivity || [] })
  useAnnexations(map, { regionsData, annexations: annexations || [] })

  useStructures(map, { showStructures, constructionMode, infrastructureMode, structuresData, zoomBucket, onStructureMove, regionsData })
  usePowerPlants(map, { showStructures, constructionMode, infrastructureMode, powerPlantsData, regionsData, onPowerPlantMove })

  // Topography layer: raster visibility + region fill fade so the relief
  // shows through while regions stay clickable.
  useEffect(() => {
    if (!map.current || !map.current.getLayer('topography')) return
    const on = !!layers.topography
    map.current.setLayoutProperty('topography', 'visibility', on ? 'visible' : 'none')
    if (map.current.getLayer('regions-fill')) {
      map.current.setPaintProperty('regions-fill', 'fill-opacity', on ? 0.08 : 0.8)
    }
  }, [layers.topography])

  useMilitaryUnits(map, mapContainer, { showMilitary, militaryUnits, onUnitClick, onUnitMoved, onUnitDropped })

  useMilPlan(map, { milPlanFeatures, milPlanActive, milPlanTool, onMilPlanClick, onMilPlanGesture, selectedMilPlanFeature, onMilPlanFeatureSelect, onMilPlanFeatureChange, milPlanTextDraft, milPlanTextStyle, onMilPlanTextCommit, onMilPlanTextCancel })

  // The node menu is anchored at the clicked node inside the map container,
  // like MapContextMenu. Its state is local: only the chosen action reaches App.
  const [infraNodeMenu, setInfraNodeMenu] = useState(null)

  useInfrastructure(map, { showInfrastructure, infrastructureMode, infraMode, constructionMode, infrastructureData, infrastructurePath, onInfraPointMove, onInfraPointDelete, onInfraNodeMenu: setInfraNodeMenu, onInfraPointConnect, selectedTroncIds, onTroncSelect, styles, regionsData })

  // A menu anchored to a node outlives neither the tool it belongs to nor a
  // camera move — a stale menu would act on the wrong node.
  useEffect(() => { if (!infrastructureMode) setInfraNodeMenu(null) }, [infrastructureMode])
  useEffect(() => {
    if (!map.current) return
    const close = () => setInfraNodeMenu(null)
    map.current.on('movestart', close)
    return () => { map.current?.off('movestart', close) }
  }, [map])

  useWorldOverlays(map, { showCities, showRoads, showRailways, showHydrography, styles, regionsData })


  useRuler(map, { rulerMode, rulerPath, onRulerPointMove, onRulerPointDelete })
  useRangeCircle(map, { rangeMode, rangeCenter, rangeRadiusKm })

  // Filters, PNG export, keyboard panning, fly-to-region, and the minimap are
  // each self-contained — extracted into single-responsibility hooks.
  useMapFilters(map, regionsData, filters)
  useMapExport(map, showToast)
  useKeyboardPan(map)
  useCenterToRegion(map, regionsData, onCenterToRegion)
  const { minimapCanvasRef, minimapVisible, handleMinimapClick } = useMinimap(map, regionsData, mapBackground)

  return (
    <>
      <div
        ref={mapContainer}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%'
        }}
      />
      <button
        type="button"
        className="map-settings-fab"
        title="Réglages d'affichage"
        aria-label="Réglages d'affichage"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => { event.preventDefault(); event.stopPropagation(); onOpenSettings?.() }}
      ><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5v.2a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1h.2a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg></button>
      <canvas
        ref={minimapCanvasRef}
        width={180}
        height={110}
        className="minimap-canvas"
        style={{ display: minimapVisible ? 'block' : 'none' }}
        onClick={handleMinimapClick}
        title="Cliquer pour se déplacer"
      />
      <CoordinatesDisplay coords={cursorCoords} />
      <MapContextMenu menu={contextMenu} onClose={closeMenu} map={map} showToast={showToast} />
      <InfraNodeMenu
        menu={infraNodeMenu}
        onClose={() => setInfraNodeMenu(null)}
        infrastructurePath={infrastructurePath}
        onConnect={onInfraConnectStart}
        onFork={onInfraPointFork}
        onDelete={onInfraPointDelete}
      />
    </>
  )
}

export default MapView
